import { ORDER_ATTACHED_FILE_TYPE } from '@/api/type/commonTypes';
import { GetOrderDetailResponse } from '@/api/type/types';
import { Images } from '@/assets';
import { useLoading } from '@/context/LoadingContext';
import { useAlert } from '@/hooks/useAlert';
import {
  BatchDownloadProgress,
  FileTransferService,
} from '@/libs/r2-service-client';
import {
  BatchDownloadRequest,
  BatchDownloadState,
} from '@/libs/r2-service-client/service';
import { fonts } from '@/theme/fonts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface OrderAttachedFilesProps {
  orderDetail: GetOrderDetailResponse;
  patientName: string;
  orderId: string;
}
const OrderAttachedFiles = ({
  orderDetail,
  patientName,
  orderId,
}: OrderAttachedFilesProps) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoading();
  const { showAlert } = useAlert();

  const onClickDownload = useCallback(
    (item: ORDER_ATTACHED_FILE_TYPE) => async () => {
      try {
        const response = await fetch(
          FileTransferService.getDownloadUri(item.id)
        );

        if (!response.ok) {
          throw new Error('파일 다운로드 실패');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);

        const a = document.createElement('a');
        a.href = url;
        a.download = item.name ?? '';
        document.body.appendChild(a);
        a.click();

        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('다운로드 실패', error);
      }
    },
    []
  );

  const onClickAllDownload = useCallback(async () => {
    startLoading();
    const fileIds = orderDetail.attachedFiles.map((item) => item.id);
    const batchDownloadRequest: BatchDownloadRequest = {
      resourceIds: fileIds,
      zipFileName: `${orderId}_${patientName}`,
    };

    try {
      const result = await FileTransferService.batchDownload(
        batchDownloadRequest,
        true
      );
      if (result.state !== BatchDownloadState.COMPLETED) {
        showAlert({
          message: t('failedToconnectTotheServerPleaseTryAgainLater'),
        });
      }
    } finally {
      stopLoading();
    }
  }, [
    orderDetail,
    orderId,
    patientName,
    showAlert,
    startLoading,
    stopLoading,
    t,
  ]);

  return (
    <Container>
      <Section>
        <TitleWrap>
          <IconWrap>
            <Img src={Images.AttachedFileIcon} alt="attached-file-icon" />
            <Title>{t('attachedFile')}</Title>
          </IconWrap>
          <FileItem>
            <CenterFileName>{t('downloadAllFiles')}</CenterFileName>
            <ImgButton
              onClick={onClickAllDownload}
              disabled={orderDetail.attachedFiles.length === 0}
            />
          </FileItem>
        </TitleWrap>
        {orderDetail.attachedFiles.length > 0 && (
          <FileList>
            {orderDetail.attachedFiles.map((item) => (
              <FileItem key={item.id}>
                <FileName>{item.name}</FileName>
                <ImgButton onClick={onClickDownload(item)} />
              </FileItem>
            ))}
          </FileList>
        )}
      </Section>
    </Container>
  );
};

export default OrderAttachedFiles;

const Container = styled.div`
  display: flex;
  gap: 20px;
  flex: 1;
  overflow-x: visible;
  overflow-y: hidden;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16.85px;
  flex: 1;
`;

const TitleWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
  padding-right: 5px;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Img = styled.img`
  width: 32px;
  height: 32px;
`;

const Title = styled.div`
  font-size: 14px;
  line-height: 120%;
  color: #769bf7;
`;

const FileList = styled.div`
  display: grid;
  grid-template-rows: repeat(5, auto); /* 세로로 5개씩 */
  grid-auto-flow: column;
  gap: 6px 20px;
  overflow-x: auto;
`;

const FileItem = styled.div`
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 5px;
  padding: 7px 10px;
  flex-shrink: 0;
  width: 211px;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  position: relative;
`;

const FileName = styled.div`
  font-family: ${fonts.family.poppins};
  font-size: 14px;
  line-height: 120%;
  color: #000000;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
`;

const CenterFileName = styled(FileName)`
  position: absolute;
  left: 23px;
`;

const ImgButton = styled.button`
  background-image: url(${Images.DownloadButtonImg});
  width: 18px;
  height: 18px;
  background-position: center;
  background-size: cover;
  flex-shrink: 0;
  margin-left: auto;

  &:disabled {
    cursor: auto;
  }
`;
